export const ACTION_ROSTER_MODES = Object.freeze({
  PER_LEARNER: 'per-learner',
  PER_OBLIGATION: 'per-obligation',
});

export const ACTION_SESSION_GROUPINGS = Object.freeze({
  CLASS: 'class',
  PHASE: 'phase',
  SCOPE: 'scope',
  SCHOOL: 'school',
});

export function createActionPolicy() {
  return {
    rosterMode: ACTION_ROSTER_MODES.PER_LEARNER,
    sessionGrouping: ACTION_SESSION_GROUPINGS.SCOPE,
  };
}

export function normalizeActionPolicy(policy = {}) {
  const requestedGrouping = policy?.sessionGrouping
    // Compatibility for the unreleased two-option Development draft.
    || (policy?.sessionScope === 'staff' ? ACTION_SESSION_GROUPINGS.SCOPE : policy?.sessionScope);
  const sessionGrouping = Object.values(ACTION_SESSION_GROUPINGS).includes(requestedGrouping)
    ? requestedGrouping
    : ACTION_SESSION_GROUPINGS.SCOPE;
  return {
    rosterMode: policy?.rosterMode === ACTION_ROSTER_MODES.PER_OBLIGATION
      ? ACTION_ROSTER_MODES.PER_OBLIGATION
      : ACTION_ROSTER_MODES.PER_LEARNER,
    sessionGrouping,
  };
}

export function resolveConsequenceType(event) {
  return event?.consequenceType || event?.value || event?.type || null;
}

export function resolveActionRosterMode(consequenceType, configuredTypes = []) {
  const configuredType = configuredTypes.find(type => type.value === consequenceType);
  return normalizeActionPolicy(configuredType?.actionPolicy).rosterMode;
}

export function resolveActionSessionGrouping(consequenceType, configuredTypes = []) {
  const configuredType = configuredTypes.find(type => type.value === consequenceType);
  return configuredType
    ? normalizeActionPolicy(configuredType.actionPolicy).sessionGrouping
    : null;
}

export function actionRosterLearnerKey(event) {
  if (event?.studentId) return `student:${event.studentId}`;
  // Never merge records whose learner identity is missing.
  return event?.id ? `record:${event.id}` : null;
}

export function getActionRecordIds(event) {
  const ids = Array.isArray(event?.rosterRecordIds) && event.rosterRecordIds.length
    ? event.rosterRecordIds
    : [event?.id];
  return [...new Set(ids.filter(Boolean))];
}

export function getActionRosterRecords(event) {
  const records = Array.isArray(event?.rosterRecords) && event.rosterRecords.length
    ? event.rosterRecords
    : [event];
  const byId = new Map();
  records.forEach(record => {
    if (record?.id) byId.set(record.id, record);
  });
  return Array.from(byId.values()).sort((left, right) => {
    const difference = eventTime(left) - eventTime(right);
    return difference || String(left.id).localeCompare(String(right.id));
  });
}

function eventTime(event) {
  const value = event?.timestamp ?? event?.issuedAt ?? event?.createdAt;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Build the working roster for configured action types.
 *
 * Per-learner mode presents one row for a learner within each consequence type
 * while retaining every separate obligation ID. The oldest obligation is the
 * visible/actionable row, so one attendance action settles exactly one
 * obligation and later obligations remain waiting.
 *
 * Per-obligation mode keeps every Queue record as its own row.
 */
export function groupActionRoster(events = [], configuredTypes = []) {
  const result = [];
  const rosterIndex = new Map();

  events.forEach(event => {
    const consequenceType = resolveConsequenceType(event);
    const rosterMode = resolveActionRosterMode(consequenceType, configuredTypes);
    const learnerKey = actionRosterLearnerKey(event);

    if (rosterMode === ACTION_ROSTER_MODES.PER_OBLIGATION || !learnerKey) {
      result.push(event);
      return;
    }

    // A learner can have one row in each action type. Never merge different
    // configured consequences merely because they concern the same learner.
    const rosterKey = `${consequenceType || 'unknown'}:${learnerKey}`;
    const existingIndex = rosterIndex.get(rosterKey);
    if (existingIndex === undefined) {
      rosterIndex.set(rosterKey, result.length);
      const recordIds = getActionRecordIds(event);
      result.push({
        ...event,
        rosterRecordIds: recordIds,
        rosterRecordCount: recordIds.length,
        rosterRecords: [event],
      });
      return;
    }

    const representative = result[existingIndex];
    const rosterRecordIds = [...new Set([
      ...getActionRecordIds(representative),
      ...getActionRecordIds(event),
    ])];
    const rosterRecords = getActionRosterRecords({
      rosterRecords: [
        ...getActionRosterRecords(representative),
        ...getActionRosterRecords(event),
      ]
    });
    const representativeTime = eventTime(representative);
    const candidateTime = eventTime(event);
    const visibleObligation = candidateTime !== null &&
      (representativeTime === null || candidateTime < representativeTime)
      ? event
      : representative;
    result[existingIndex] = {
      ...visibleObligation,
      rosterRecordIds,
      rosterRecordCount: rosterRecordIds.length,
      rosterRecords,
    };
  });

  return result;
}

export function countActionRoster(events = [], configuredTypes = []) {
  return groupActionRoster(events, configuredTypes).length;
}
